import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { getAttendeePreferenceLabels, normalizeRsvpDetails, normalizeRsvpOptions } from "./rsvpOptions";

/** True when balance should be hidden in UI (null, empty, NaN, or <= 0). */
export const isBankEmpty = (value) => {
  if (value === null || value === undefined || value === "") return true;
  const n = parseInt(String(value), 10);
  return Number.isNaN(n) || n <= 0;
};

const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
// Desired file extesion
const fileExtension = ".xlsx";

const getPartySize = (contact) => {
  const parsed = parseInt(String(contact?.howMany || "1"), 10);
  return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
};

const getTableDetails = (contact, seatingTables = []) => {
  const table = seatingTables.find((item) => item.id === contact.tableNum);
  if (!table) return { name: "ללא שולחן", number: "-" };
  return { name: table.name || "-", number: table.number || "-" };
};

export const determinStatus = (value) => {
  if (value === "pending") {
    return "ממתינים לתשובה/לא יודעים";
  }
  if (value === "approved") {
    return "אישרו הגעה";
  }
  if (value === "notComing") {
    return "לא מגיעים";
  }
  if (value === "all") {
    return " לכולם";
  }
};
export const determinStatusClass = (value) => {
  if (value === "pending") {
    return "text-warning";
  }
  if (value === "approved") {
    return "text-success";
  }
  if (value === "notComing") {
    return "text-danger";
  }
  if (value === "all") {
    return "text-muted";
  }
};
export const exportToSpreadsheet = (data, fileName, seatingTables = [], rsvpOptionsValue = null) => {
    //Create a new Work Sheet using the data stored in an Array of Arrays.
    let temp = [];
    data.forEach((el) => {
      if (el.name !== "") {
        temp.push(el);
      }
    });
    let translated = temp.map((el) => {
      const row = {
        שם: el.name,
        סטאטוס: determinStatus(el.status),
        טלפון: el.phoneNumber,
        מוזמנים: el.howMany,
      };
      const tableDetails = getTableDetails(el, seatingTables);
      row["שם שולחן"] = tableDetails.name;
      row["מספר שולחן"] = tableDetails.number;
      if (rsvpOptionsValue) {
        const options = normalizeRsvpOptions(rsvpOptionsValue);
        const details = normalizeRsvpDetails(el.rsvpDetails, getPartySize(el));
        if (options.transportation.enabled) {
          row["הסעה"] = !details.transportation.answered ? "לא נבחרה תשובה" : details.transportation.needsRide ? "כן" : "לא";
          row["מקומות בהסעה"] = details.transportation.needsRide ? details.transportation.seats : 0;
        }
        details.attendees.forEach((attendee, index) => {
          row[`אורח ${index + 1} - אפשרויות`] = getAttendeePreferenceLabels(attendee, options).join(", ") || "אין העדפות מיוחדות";
        });
      }
      return row;
    });
    const workSheet = XLSX.utils.json_to_sheet(translated);
    // Generate a Work Book containing the above sheet.
    const workBook = {
      Sheets: { data: workSheet, cols: [] },
      SheetNames: ["data"],
    };
    // Exporting the file with the desired name and extension.
    const excelBuffer = XLSX.write(workBook, { bookType: "xlsx", type: "array" });
    const fileData = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(fileData, fileName + fileExtension);
  };

export const exportRsvpSummaryToSpreadsheet = ({ summary, options: optionsValue }, fileName) => {
  const options = normalizeRsvpOptions(optionsValue);
  const { totals, missing, dishCombinations } = summary;
  const transportationNote = missing.transportation > 0
    ? `${missing.transportation} הזמנות ללא תשובה חושבו כלא צריכות הסעה`
    : "";
  const rows = [
    ...(options.transportation.enabled ? [
      { קטגוריה: "הסעה", פירוט: "מקומות בהסעה", כמות: totals.transportationSeats, הערה: transportationNote },
    ] : []),
    ...(options.gender.enabled ? [
      { קטגוריה: "חלוקה", פירוט: "גברים", כמות: totals.male },
      { קטגוריה: "חלוקה", פירוט: "נשים", כמות: totals.female },
    ] : []),
    ...dishCombinations.map((dish) => ({
      קטגוריה: "מנות מדויקות להכנה",
      פירוט: dish.label,
      כמות: dish.count,
      הערה: dish.assumedCount > 0 ? `מתוכן ${dish.assumedCount} חושבו כברירת מחדל בגלל תשובה חסרה` : "",
    })),
    ...(missing.gender > 0 ? [{ קטגוריה: "חסרות תשובות", פירוט: "אורחים ללא בחירת גבר/אישה", כמות: missing.gender }] : []),
  ];
  const workSheet = XLSX.utils.json_to_sheet(rows);
  workSheet["!cols"] = [{ wch: 20 }, { wch: 42 }, { wch: 12 }, { wch: 58 }];
  workSheet["!views"] = [{ rightToLeft: true }];
  const workBook = { Sheets: { "סיכום העדפות": workSheet }, SheetNames: ["סיכום העדפות"] };
  const excelBuffer = XLSX.write(workBook, { bookType: "xlsx", type: "array" });
  FileSaver.saveAs(new Blob([excelBuffer], { type: fileType }), fileName + fileExtension);
};

export const exportSeatingToSpreadsheet = ({ tables = [], contacts = [] }, fileName) => {
  const rows = [];
  tables.forEach((table) => {
    const assignedContacts = contacts.filter((contact) => contact.tableNum === table.id);
    if (assignedContacts.length === 0) {
      rows.push({
        "שם שולחן": table.name,
        "מספר שולחן": table.number,
        "שם אורח": "אין אורחים",
        "גודל קבוצה": "",
        טלפון: "",
      });
      return;
    }
    assignedContacts.forEach((contact) => {
      const partySize = getPartySize(contact);
      rows.push({
        "שם שולחן": table.name,
        "מספר שולחן": table.number,
        "שם אורח": contact.name,
        "גודל קבוצה": partySize,
        טלפון: contact.phoneNumber,
      });
    });
  });
  const workSheet = XLSX.utils.json_to_sheet(rows);
  const workBook = {
    Sheets: { data: workSheet, cols: [] },
    SheetNames: ["data"],
  };
  const excelBuffer = XLSX.write(workBook, { bookType: "xlsx", type: "array" });
  const fileData = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(fileData, fileName + fileExtension);
};
